import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { Search, Download, Eye } from "lucide-react";
import { exportToCSV, exportToExcel } from "../../lib/exporter";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [selectedStudent, setSelectedStudent] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDocs(collection(db, "students"));
        setStudents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, []);

  const filtered = students.filter((s) => {
    const matchesSearch = !search ||
      [s.fullName, s.email, s.phone, s.studentId, s.rollNumber, s.college]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(search.toLowerCase()));
    const matchesPay = paymentFilter === "all" || s.paymentStatus === paymentFilter;
    return matchesSearch && matchesPay;
  });

  const buildExport = () =>
    filtered.map((s) => ({
      "Student ID": s.studentId,
      "Full Name": s.fullName,
      "Email": s.email,
      "Phone": s.phone,
      "Roll Number": s.rollNumber,
      "University": s.university,
      "College": s.college,
      "Course": s.course,
      "Major": s.majorSubject,
      "Minor": s.minorSubject,
      "Internship": s.selectedInternship,
      "Payment Status": s.paymentStatus,
      "Amount Paid": s.amountPaid,
      "Attendance %": s.attendancePercentage,
      "Certificate Status": s.certificateStatus,
    }));

  return (
    <div data-testid="admin-students">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Student Management</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">All Students</h1>
          <p className="mt-1 text-slate-600">{filtered.length} of {students.length} students</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportToCSV(buildExport(), "students.csv")} data-testid="export-students-csv" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-semibold">
            <Download className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => exportToExcel(buildExport(), "students.xlsx", "Students")} data-testid="export-students-excel" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
            <Download className="w-4 h-4" /> Excel
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="students-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, phone, college..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
            />
          </div>
          <select
            data-testid="students-filter-select"
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="all">All Payments</option>
            <option value="paid">Paid</option>
            <option value="free_via_coupon">Coupon</option>
            <option value="pending">Pending</option>
          </select>
        </div>

        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="no-students">No students found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-6 py-3 font-semibold">Student ID</th>
                  <th className="text-left px-6 py-3 font-semibold">Name</th>
                  <th className="text-left px-6 py-3 font-semibold">College</th>
                  <th className="text-left px-6 py-3 font-semibold">Program</th>
                  <th className="text-left px-6 py-3 font-semibold">Payment</th>
                  <th className="text-left px-6 py-3 font-semibold">Attendance</th>
                  <th className="text-left px-6 py-3 font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-6 py-3 font-mono text-xs">{s.studentId}</td>
                    <td className="px-6 py-3 font-medium text-slate-900">
                      <div>{s.fullName}</div>
                      <div className="text-xs text-slate-500">{s.email}</div>
                    </td>
                    <td className="px-6 py-3 text-slate-700">{s.college}</td>
                    <td className="px-6 py-3 text-slate-700">{s.selectedInternship}</td>
                    <td className="px-6 py-3">
                      <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold capitalize">
                        {s.paymentStatus}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-slate-900">{s.attendancePercentage || 0}%</td>
                    <td className="px-6 py-3">
                      <button onClick={() => setSelectedStudent(s)} data-testid={`view-student-${s.studentId}`} className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs font-semibold">
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!selectedStudent} onOpenChange={() => setSelectedStudent(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedStudent?.fullName}</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {Object.entries(selectedStudent).filter(([k]) => !["id", "uid", "createdAt"].includes(k)).map(([k, v]) => (
                  <div key={k} className="border-b border-slate-100 pb-2">
                    <div className="text-xs text-slate-500 capitalize">{k.replace(/([A-Z])/g, " $1")}</div>
                    <div className="font-medium text-slate-900">{String(v) || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStudents;
